import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { X, Heart, Share2, Sparkles, Wind, Volume2, VolumeX, Play, Pause, RotateCcw, Flame, Trophy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getGenderEmoji } from "../utils/genderEmojis";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VibeOutHub = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const userGender = user?.wellness_profile?.gender || "female";
  const [activeMode, setActiveMode] = useState(null);
  const [affirmation, setAffirmation] = useState(null);
  const [breathingPatterns, setBreathingPatterns] = useState([]);
  const [soundscapes, setSoundscapes] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [breathPhase, setBreathPhase] = useState(null);
  const [breathCount, setBreathCount] = useState(0);
  const [isBreathing, setIsBreathing] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const [selectedSound, setSelectedSound] = useState(null);
  const [breathSoundEnabled, setBreathSoundEnabled] = useState(true);
  const [soundDuration, setSoundDuration] = useState(0);
  const [timerGoal, setTimerGoal] = useState(null); // null = unlimited, or seconds
  const [timerRemaining, setTimerRemaining] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const canvasRef = useRef(null);
  const breathTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const breathAudioRef = useRef(null);
  const soundTimerRef = useRef(null);
  const goalTimerRef = useRef(null);
  const sessionStartRef = useRef(null);
  
  // Timer presets in minutes
  const timerPresets = [
    { label: "∞", value: null, desc: "Unlimited" },
    { label: "5m", value: 5 * 60, desc: "5 minutes" },
    { label: "10m", value: 10 * 60, desc: "10 minutes" },
    { label: "15m", value: 15 * 60, desc: "15 minutes" },
    { label: "30m", value: 30 * 60, desc: "30 minutes" },
  ];

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  useEffect(() => {
    if (isOpen) {
      fetchBreathingPatterns();
      fetchSoundscapes();
      fetchStreakData();
    }
    return () => {
      stopBreathing();
      stopSound();
    };
  }, [isOpen]);

  const fetchBreathingPatterns = async () => {
    try {
      const res = await axios.get(`${API}/vibe-out/breathing-patterns`, getAuthHeader());
      setBreathingPatterns(res.data.patterns);
    } catch (err) {
      console.error("Error fetching patterns:", err);
    }
  };

  const fetchSoundscapes = async () => {
    try {
      const res = await axios.get(`${API}/vibe-out/soundscapes`, getAuthHeader());
      setSoundscapes(res.data.soundscapes);
    } catch (err) {
      console.error("Error fetching soundscapes:", err);
    }
  };

  const fetchStreakData = async () => {
    try {
      const res = await axios.get(`${API}/vibe-out/meditation/streak`, getAuthHeader());
      setStreakData(res.data);
    } catch (err) {
      console.error("Error fetching streak:", err);
    }
  };

  const logMeditationSession = async (durationSeconds, soundType) => {
    try {
      const res = await axios.post(
        `${API}/vibe-out/meditation/log?duration_seconds=${durationSeconds}&sound_type=${encodeURIComponent(soundType)}`,
        {},
        getAuthHeader()
      );
      
      // Update streak data
      if (res.data.streak) {
        setStreakData(prev => prev ? { ...prev, streak: res.data.streak } : null);
      }
      
      // Show new milestone achievement
      if (res.data.new_milestone) {
        toast.success(
          `🎉 New Milestone: ${res.data.new_milestone.badge} ${res.data.new_milestone.title}!`,
          { duration: 5000 }
        );
      }
      
      // Refresh full streak data
      fetchStreakData();
    } catch (err) {
      console.error("Error logging meditation:", err);
    }
  };

  const fetchAffirmation = async () => {
    try {
      const res = await axios.get(`${API}/vibe-out/affirmations`, getAuthHeader());
      setAffirmation(res.data);
    } catch (err) {
      toast.error("Couldn't load affirmation");
    }
  };

  const saveAffirmation = async () => {
    if (!affirmation) return;
    try {
      await axios.post(`${API}/vibe-out/favorite-affirmation?text=${encodeURIComponent(affirmation.text)}`, {}, getAuthHeader());
      toast.success("Affirmation saved to favorites 💜");
    } catch (err) {
      toast.error("Couldn't save affirmation");
    }
  };

  const shareAffirmation = () => {
    if (!affirmation) return;
    if (navigator.share) {
      navigator.share({
        title: "Daily Affirmation from Lala Land",
        text: `${affirmation.emoji} "${affirmation.text}" - via Rooted Wellness`,
        url: "https://www.stayrootedlala.com"
      });
    } else {
      navigator.clipboard.writeText(`${affirmation.emoji} "${affirmation.text}"`);
      toast.success("Copied to clipboard!");
    }
  };

  // Breathing Exercise Logic - Using refs to avoid stale closure
  const isBreathingRef = useRef(false);
  const breathPhaseRef = useRef(null);
  
  // Breath Sound Functions
  const initBreathAudio = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    return ctx;
  };

  const playBreathSound = async (phase, duration) => {
    if (!breathSoundEnabled) return;
    
    try {
      const ctx = await initBreathAudio();
      
      // Stop previous breath sound
      if (breathAudioRef.current) {
        try {
          breathAudioRef.current.oscillator.stop();
          breathAudioRef.current.oscillator2?.stop();
        } catch (e) {}
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      const oscillator2 = ctx.createOscillator(); // Harmony
      const gainNode2 = ctx.createGain();
      
      // Different sounds for different phases
      if (phase === 'inhale') {
        // Rising tone - starts low, goes high
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(180, ctx.currentTime);
        oscillator.frequency.linearRampToValueAtTime(320, ctx.currentTime + duration / 1000);
        
        // Harmony (5th above)
        oscillator2.type = 'sine';
        oscillator2.frequency.setValueAtTime(270, ctx.currentTime);
        oscillator2.frequency.linearRampToValueAtTime(480, ctx.currentTime + duration / 1000);
        
        // Volume envelope - swell up
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + duration / 2000);
        gainNode.gain.linearRampToValueAtTime(0.1, ctx.currentTime + duration / 1000);
        
        gainNode2.gain.setValueAtTime(0, ctx.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.08, ctx.currentTime + duration / 2000);
        gainNode2.gain.linearRampToValueAtTime(0.05, ctx.currentTime + duration / 1000);
        
      } else if (phase === 'exhale') {
        // Falling tone - starts high, goes low
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(320, ctx.currentTime);
        oscillator.frequency.linearRampToValueAtTime(150, ctx.currentTime + duration / 1000);
        
        oscillator2.type = 'sine';
        oscillator2.frequency.setValueAtTime(480, ctx.currentTime);
        oscillator2.frequency.linearRampToValueAtTime(225, ctx.currentTime + duration / 1000);
        
        // Volume envelope - fade out
        gainNode.gain.setValueAtTime(0.12, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.08, ctx.currentTime + duration / 2000);
        gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration / 1000);
        
        gainNode2.gain.setValueAtTime(0.06, ctx.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.03, ctx.currentTime + duration / 2000);
        gainNode2.gain.linearRampToValueAtTime(0, ctx.currentTime + duration / 1000);
        
      } else if (phase === 'hold1' || phase === 'hold2') {
        // Soft sustained drone
        oscillator.type = 'sine';
        const holdFreq = phase === 'hold1' ? 280 : 200;
        oscillator.frequency.setValueAtTime(holdFreq, ctx.currentTime);
        
        oscillator2.type = 'triangle';
        oscillator2.frequency.setValueAtTime(holdFreq * 1.5, ctx.currentTime);
        
        // Gentle sustain
        gainNode.gain.setValueAtTime(0.06, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.08, ctx.currentTime + duration / 2000);
        gainNode.gain.linearRampToValueAtTime(0.04, ctx.currentTime + duration / 1000);
        
        gainNode2.gain.setValueAtTime(0.03, ctx.currentTime);
      }
      
      oscillator.connect(gainNode);
      oscillator2.connect(gainNode2);
      gainNode.connect(ctx.destination);
      gainNode2.connect(ctx.destination);
      
      oscillator.start();
      oscillator2.start();
      
      // Schedule stop
      oscillator.stop(ctx.currentTime + duration / 1000 + 0.1);
      oscillator2.stop(ctx.currentTime + duration / 1000 + 0.1);
      
      breathAudioRef.current = { oscillator, oscillator2 };
      
    } catch (err) {
      console.error("Breath sound error:", err);
    }
  };

  const stopBreathSound = () => {
    if (breathAudioRef.current) {
      try {
        breathAudioRef.current.oscillator.stop();
        breathAudioRef.current.oscillator2?.stop();
      } catch (e) {}
      breathAudioRef.current = null;
    }
  };

  const startBreathing = (pattern) => {
    setSelectedPattern(pattern);
    setIsBreathing(true);
    isBreathingRef.current = true;
    setBreathCount(0);
    runBreathCycle(pattern, 0);
  };

  const runBreathCycle = (pattern, cycle) => {
    if (cycle >= pattern.cycles) {
      setIsBreathing(false);
      isBreathingRef.current = false;
      setBreathPhase(null);
      breathPhaseRef.current = null;
      stopBreathSound();
      toast.success(`Breathing complete! ${getGenderEmoji("yoga", userGender)} You did amazing!`);
      return;
    }

    const phases = [
      { phase: "inhale", duration: pattern.inhale * 1000, text: "Breathe In", scale: 1 },
      { phase: "hold1", duration: pattern.hold1 * 1000, text: "Hold", scale: 1 },
      { phase: "exhale", duration: pattern.exhale * 1000, text: "Breathe Out", scale: 0.4 },
      { phase: "hold2", duration: pattern.hold2 * 1000, text: "Hold", scale: 0.4 }
    ].filter(p => p.duration > 0);

    let phaseIndex = 0;

    const nextPhase = () => {
      if (!isBreathingRef.current) return;
      
      if (phaseIndex >= phases.length) {
        setBreathCount(prev => prev + 1);
        setTimeout(() => runBreathCycle(pattern, cycle + 1), 100);
        return;
      }

      const currentPhase = phases[phaseIndex];
      setBreathPhase(currentPhase);
      breathPhaseRef.current = currentPhase;
      
      // Play breath sound for this phase
      playBreathSound(currentPhase.phase, currentPhase.duration);
      
      phaseIndex++;

      breathTimerRef.current = setTimeout(nextPhase, currentPhase.duration);
    };

    nextPhase();
  };

  const stopBreathing = () => {
    setIsBreathing(false);
    isBreathingRef.current = false;
    setBreathPhase(null);
    breathPhaseRef.current = null;
    setSelectedPattern(null);
    setBreathCount(0);
    stopBreathSound();
    if (breathTimerRef.current) {
      clearTimeout(breathTimerRef.current);
    }
  };

  // Satisfying Visuals - Ripple Effect
  const createRipple = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log("Canvas not found");
      return;
    }
    
    const rect = canvas.getBoundingClientRect();
    
    // Scale coordinates from display size to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    console.log(`Creating ripple at (${x}, ${y})`);
    
    const colors = ["#8B5CF6", "#EC4899", "#10B981", "#F59E0B", "#6366F1", "#14B8A6", "#F472B6"];
    const newRipple = {
      id: Date.now() + Math.random(),
      x,
      y,
      radius: 5, // Start with visible radius
      maxRadius: 100 + Math.random() * 60,
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: 1
    };
    
    setRipples(prev => {
      const updated = [...prev, newRipple];
      ripplesRef.current = updated;
      return updated;
    });
  };

  const handleCanvasClick = (e) => {
    e.preventDefault();
    createRipple(e.clientX, e.clientY);
  };

  const handleCanvasTouch = (e) => {
    e.preventDefault();
    const touch = e.touches[0] || e.changedTouches[0];
    if (touch) {
      createRipple(touch.clientX, touch.clientY);
    }
  };

  // Animation ref to track animation frame
  const animationFrameRef = useRef(null);
  const ripplesRef = useRef([]);

  // Keep ripplesRef in sync with ripples state
  useEffect(() => {
    ripplesRef.current = ripples;
  }, [ripples]);

  // Start animation loop when entering visuals mode
  useEffect(() => {
    if (activeMode !== "visuals") {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw gradient background
      const gradient = ctx.createRadialGradient(canvas.width/2, canvas.height/2, 0, canvas.width/2, canvas.height/2, canvas.width/2);
      gradient.addColorStop(0, "rgba(139, 92, 246, 0.15)");
      gradient.addColorStop(1, "rgba(236, 72, 153, 0.08)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw ripples
      const currentRipples = ripplesRef.current;
      const updatedRipples = currentRipples.map(ripple => ({
        ...ripple,
        radius: ripple.radius + 2,
        opacity: Math.max(0, 1 - ripple.radius / ripple.maxRadius)
      })).filter(r => r.opacity > 0);
      
      updatedRipples.forEach(ripple => {
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = ripple.color;
        ctx.lineWidth = 4;
        ctx.globalAlpha = ripple.opacity;
        ctx.stroke();
        
        // Inner glow
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius * 0.8, 0, Math.PI * 2);
        ctx.strokeStyle = ripple.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = ripple.opacity * 0.5;
        ctx.stroke();
        
        ctx.globalAlpha = 1;
      });
      
      // Update state only if ripples changed
      if (JSON.stringify(updatedRipples) !== JSON.stringify(currentRipples)) {
        setRipples(updatedRipples);
      }
      
      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [activeMode]);

  // Sound Bath - Web Audio API for frequencies and nature sounds
  const playSound = async (soundscape) => {
    // Stop any existing sound
    stopSound();
    
    setSelectedSound(soundscape);
    
    try {
      // Use Web Audio API for all sounds
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Resume AudioContext if suspended (required for mobile)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // Create sounds based on type
      if (soundscape.frequency && (soundscape.frequency === "432Hz" || soundscape.frequency === "528Hz" || soundscape.frequency === "10Hz")) {
        // Frequency tones
        const freq = soundscape.frequency === "432Hz" ? 432 : 
                     soundscape.frequency === "528Hz" ? 528 : 174;
        
        const oscillator = ctx.createOscillator();
        const oscillator2 = ctx.createOscillator();
        const masterGain = ctx.createGain();
        const gainNode1 = ctx.createGain();
        const gainNode2 = ctx.createGain();
        
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
        oscillator2.type = "sine";
        oscillator2.frequency.setValueAtTime(freq * 1.005, ctx.currentTime);
        
        gainNode1.gain.setValueAtTime(0.4, ctx.currentTime);
        gainNode2.gain.setValueAtTime(0.25, ctx.currentTime);
        masterGain.gain.setValueAtTime(0, ctx.currentTime);
        masterGain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 1);
        
        oscillator.connect(gainNode1);
        oscillator2.connect(gainNode2);
        gainNode1.connect(masterGain);
        gainNode2.connect(masterGain);
        masterGain.connect(ctx.destination);
        
        oscillator.start();
        oscillator2.start();
        
        oscillatorRef.current = { type: 'frequency', nodes: [oscillator, oscillator2], master: masterGain };
        toast.success(`🔔 Playing ${soundscape.frequency} - Turn up your volume!`);
        
      } else if (soundscape.id === "rain") {
        // Rain - white noise filtered
        const bufferSize = 2 * ctx.sampleRate;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          output[i] = Math.random() * 2 - 1;
        }
        
        const whiteNoise = ctx.createBufferSource();
        whiteNoise.buffer = noiseBuffer;
        whiteNoise.loop = true;
        
        const lowpass = ctx.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.setValueAtTime(1000, ctx.currentTime);
        
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 1);
        
        whiteNoise.connect(lowpass);
        lowpass.connect(gainNode);
        gainNode.connect(ctx.destination);
        whiteNoise.start();
        
        oscillatorRef.current = { type: 'noise', nodes: [whiteNoise], master: gainNode };
        toast.success(`🌧️ Rain sounds - Turn up your volume!`);
        
      } else if (soundscape.id === "ocean") {
        // Ocean - modulated noise
        const bufferSize = 2 * ctx.sampleRate;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          output[i] = Math.random() * 2 - 1;
        }
        
        const whiteNoise = ctx.createBufferSource();
        whiteNoise.buffer = noiseBuffer;
        whiteNoise.loop = true;
        
        const lowpass = ctx.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.setValueAtTime(500, ctx.currentTime);
        
        // LFO for wave effect
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.frequency.setValueAtTime(0.1, ctx.currentTime); // Slow wave
        lfoGain.gain.setValueAtTime(200, ctx.currentTime);
        lfo.connect(lfoGain);
        lfoGain.connect(lowpass.frequency);
        
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 1);
        
        whiteNoise.connect(lowpass);
        lowpass.connect(gainNode);
        gainNode.connect(ctx.destination);
        whiteNoise.start();
        lfo.start();
        
        oscillatorRef.current = { type: 'noise', nodes: [whiteNoise, lfo], master: gainNode };
        toast.success(`🌊 Ocean waves - Turn up your volume!`);
        
      } else if (soundscape.id === "forest") {
        // Forest - layered chirps and rustling
        const bufferSize = 2 * ctx.sampleRate;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          output[i] = Math.random() * 2 - 1;
        }
        
        const whiteNoise = ctx.createBufferSource();
        whiteNoise.buffer = noiseBuffer;
        whiteNoise.loop = true;
        
        // Bandpass for wind/leaves rustling
        const bandpass = ctx.createBiquadFilter();
        bandpass.type = 'bandpass';
        bandpass.frequency.setValueAtTime(2000, ctx.currentTime);
        bandpass.Q.setValueAtTime(0.5, ctx.currentTime);
        
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 1);
        
        // Add bird-like chirp
        const chirp = ctx.createOscillator();
        const chirpGain = ctx.createGain();
        chirp.type = 'sine';
        chirp.frequency.setValueAtTime(2000, ctx.currentTime);
        chirpGain.gain.setValueAtTime(0, ctx.currentTime);
        
        // Chirp pattern
        const chirpPattern = () => {
          if (!oscillatorRef.current) return;
          chirpGain.gain.setValueAtTime(0.1, ctx.currentTime);
          chirpGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
          setTimeout(chirpPattern, 2000 + Math.random() * 3000);
        };
        setTimeout(chirpPattern, 1000);
        
        whiteNoise.connect(bandpass);
        bandpass.connect(gainNode);
        chirp.connect(chirpGain);
        chirpGain.connect(gainNode);
        gainNode.connect(ctx.destination);
        whiteNoise.start();
        chirp.start();
        
        oscillatorRef.current = { type: 'noise', nodes: [whiteNoise, chirp], master: gainNode };
        toast.success(`🌲 Forest ambience - Turn up your volume!`);
        
      } else if (soundscape.id === "singing-bowl") {
        // Tibetan singing bowl - rich harmonics
        const fundamental = 256; // C4
        const harmonics = [1, 2, 3, 4.5, 5.5];
        const oscillators = [];
        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(0, ctx.currentTime);
        masterGain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 2);
        
        harmonics.forEach((mult, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(fundamental * mult, ctx.currentTime);
          gain.gain.setValueAtTime(0.3 / (i + 1), ctx.currentTime);
          osc.connect(gain);
          gain.connect(masterGain);
          osc.start();
          oscillators.push(osc);
        });
        
        masterGain.connect(ctx.destination);
        oscillatorRef.current = { type: 'bowl', nodes: oscillators, master: masterGain };
        toast.success(`🔔 Singing bowl resonating - Turn up your volume!`);
      }
      
      setIsSoundPlaying(true);
      setSoundDuration(0);
      sessionStartRef.current = Date.now(); // Track session start time
      
      // Start duration timer - counts up every second
      soundTimerRef.current = setInterval(() => {
        setSoundDuration(prev => prev + 1);
      }, 1000);
      
      // Start goal timer if a goal is set
      if (timerGoal) {
        startGoalTimer(timerGoal);
      }
      
    } catch (err) {
      console.error("Audio error:", err);
      toast.error("Unable to play audio. Check your volume and try again.");
      setSelectedSound(null);
    }
  };

  const stopSound = () => {
    // Clear the duration timer
    if (soundTimerRef.current) {
      clearInterval(soundTimerRef.current);
      soundTimerRef.current = null;
    }
    
    // Clear the goal timer
    if (goalTimerRef.current) {
      clearInterval(goalTimerRef.current);
      goalTimerRef.current = null;
    }
    
    if (oscillatorRef.current) {
      try {
        const { nodes, master } = oscillatorRef.current;
        
        // Fade out
        if (master && audioContextRef.current) {
          master.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 0.3);
        }
        
        // Stop all nodes after fade
        setTimeout(() => {
          if (nodes) {
            nodes.forEach(node => {
              try {
                node.stop();
              } catch (e) {
                // Node might already be stopped
              }
            });
          }
        }, 350);
        
      } catch (e) {
        console.error("Error stopping sound:", e);
      }
      oscillatorRef.current = null;
    }
    
    // Log the meditation session if it was at least 30 seconds
    if (sessionStartRef.current && selectedSound) {
      const sessionDuration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      if (sessionDuration >= 30) {
        logMeditationSession(sessionDuration, selectedSound.name);
      }
      sessionStartRef.current = null;
    }
    
    setIsSoundPlaying(false);
    setSelectedSound(null);
    setSoundDuration(0);
    setTimerRemaining(null);
  };
  
  // Play a gentle completion chime
  const playCompletionChime = async () => {
    try {
      const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();
      
      // Create a pleasant bell-like chime
      const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 (C major chord)
      
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + 2.5);
      });
    } catch (e) {
      console.error("Chime error:", e);
    }
  };
  
  // Start goal timer when sound starts playing with a goal set
  const startGoalTimer = (goalSeconds) => {
    if (!goalSeconds) return;
    
    setTimerRemaining(goalSeconds);
    
    goalTimerRef.current = setInterval(() => {
      setTimerRemaining(prev => {
        if (prev <= 1) {
          // Timer complete!
          clearInterval(goalTimerRef.current);
          goalTimerRef.current = null;
          playCompletionChime();
          toast.success("🔔 Meditation complete! Namaste ✨", { duration: 5000 });
          setTimeout(() => stopSound(), 2000); // Stop after chime
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // Format seconds to MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const modes = [
    { id: "breathing", name: "Breathing", emoji: "🌬️", color: "from-blue-500 to-cyan-500", desc: "Guided breathwork" },
    { id: "visuals", name: "Visuals", emoji: "✨", color: "from-violet-500 to-pink-500", desc: "Satisfying ripples" },
    { id: "sounds", name: "Sound Bath", emoji: "🔔", color: "from-amber-500 to-orange-500", desc: "Healing frequencies" },
    { id: "affirmations", name: "Affirmations", emoji: "💜", color: "from-purple-500 to-indigo-500", desc: "Spiritual wisdom" }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 p-4 text-white z-10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              Vibe Out Hub
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-white/80 mt-1">Take a moment to reset your energy ✨</p>
        </div>

        <div className="p-4 space-y-4">
          {/* Mode Selection */}
          {!activeMode && (
            <div className="grid grid-cols-2 gap-3">
              {modes.map(mode => (
                <button
                  key={mode.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Clicked mode:", mode.id);
                    setActiveMode(mode.id);
                    if (mode.id === "affirmations") fetchAffirmation();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    console.log("Touched mode:", mode.id);
                    setActiveMode(mode.id);
                    if (mode.id === "affirmations") fetchAffirmation();
                  }}
                  className={`p-4 rounded-xl bg-gradient-to-br ${mode.color} text-white text-left transition-transform hover:scale-105 active:scale-95 cursor-pointer touch-manipulation select-none`}
                  data-testid={`vibe-mode-${mode.id}`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className="text-3xl block mb-2 pointer-events-none">{mode.emoji}</span>
                  <p className="font-semibold pointer-events-none">{mode.name}</p>
                  <p className="text-xs opacity-80 pointer-events-none">{mode.desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* Back Button */}
          {activeMode && (
            <button
              onClick={(e) => {
                e.preventDefault();
                stopBreathing();
                stopSound();
                setActiveMode(null);
                setAffirmation(null);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                stopBreathing();
                stopSound();
                setActiveMode(null);
                setAffirmation(null);
              }}
              className="mb-2 px-3 py-2 rounded-lg hover:bg-accent text-sm text-muted-foreground touch-manipulation"
            >
              ← Back to menu
            </button>
          )}

          {/* BREATHING MODE */}
          {activeMode === "breathing" && (
            <div className="space-y-4">
              {!isBreathing && !selectedPattern && (
                <>
                  <h3 className="font-semibold text-center">Choose Your Breath</h3>
                  <p className="text-xs text-muted-foreground text-center">Tap a pattern to begin</p>
                  <div className="space-y-2">
                    {breathingPatterns.map(pattern => (
                      <button 
                        key={pattern.id} 
                        className="w-full p-3 rounded-xl bg-card border-2 border-transparent hover:border-blue-500/50 transition-colors cursor-pointer touch-manipulation select-none text-left active:scale-[0.98]"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          startBreathing(pattern);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          startBreathing(pattern);
                        }}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-start gap-3 pointer-events-none">
                          <span className="text-2xl">{pattern.emoji}</span>
                          <div className="flex-1">
                            <p className="font-medium">{pattern.name}</p>
                            <p className="text-xs text-muted-foreground">{pattern.description}</p>
                            <div className="flex gap-2 mt-2">
                              {pattern.benefits.slice(0, 2).map((b, i) => (
                                <span key={i} className="text-[10px] bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full">
                                  {b}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Breathing Animation - Pattern Specific */}
              {isBreathing && selectedPattern && (
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    {selectedPattern.name} • Cycle {breathCount + 1}/{selectedPattern.cycles}
                  </p>
                  
                  {/* BOX BREATHING - Square visual */}
                  {selectedPattern.id === 'box' && (
                    <div className="relative w-64 h-64 mx-auto mb-4 flex items-center justify-center">
                      {/* Ambient glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur-xl" />
                      
                      {/* The Box */}
                      <div className="relative w-48 h-48">
                        {/* Box outline */}
                        <div className="absolute inset-0 rounded-xl border-4 border-indigo-300/30" />
                        
                        {/* Animated sides */}
                        {/* Top side - Inhale */}
                        <div 
                          className={`absolute top-0 left-0 h-1 rounded-full transition-all ease-linear ${
                            breathPhase?.phase === 'inhale' ? 'bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 shadow-lg shadow-purple-500/50' : 'bg-indigo-300/30'
                          }`}
                          style={{ 
                            width: breathPhase?.phase === 'inhale' ? '100%' : '0%',
                            transitionDuration: breathPhase?.phase === 'inhale' ? `${breathPhase.duration}ms` : '0ms'
                          }}
                        />
                        {/* Right side - Hold 1 */}
                        <div 
                          className={`absolute top-0 right-0 w-1 rounded-full transition-all ease-linear ${
                            breathPhase?.phase === 'hold1' ? 'bg-gradient-to-b from-indigo-500 via-purple-500 to-cyan-500 shadow-lg shadow-cyan-500/50' : 'bg-indigo-300/30'
                          }`}
                          style={{ 
                            height: breathPhase?.phase === 'hold1' ? '100%' : '0%',
                            transitionDuration: breathPhase?.phase === 'hold1' ? `${breathPhase.duration}ms` : '0ms'
                          }}
                        />
                        {/* Bottom side - Exhale */}
                        <div 
                          className={`absolute bottom-0 right-0 h-1 rounded-full transition-all ease-linear ${
                            breathPhase?.phase === 'exhale' ? 'bg-gradient-to-l from-cyan-500 via-teal-500 to-emerald-500 shadow-lg shadow-teal-500/50' : 'bg-indigo-300/30'
                          }`}
                          style={{ 
                            width: breathPhase?.phase === 'exhale' ? '100%' : '0%',
                            transitionDuration: breathPhase?.phase === 'exhale' ? `${breathPhase.duration}ms` : '0ms'
                          }}
                        />
                        {/* Left side - Hold 2 */}
                        <div 
                          className={`absolute bottom-0 left-0 w-1 rounded-full transition-all ease-linear ${
                            breathPhase?.phase === 'hold2' ? 'bg-gradient-to-t from-emerald-500 via-teal-500 to-pink-500 shadow-lg shadow-emerald-500/50' : 'bg-indigo-300/30'
                          }`}
                          style={{ 
                            height: breathPhase?.phase === 'hold2' ? '100%' : '0%',
                            transitionDuration: breathPhase?.phase === 'hold2' ? `${breathPhase.duration}ms` : '0ms'
                          }}
                        />
                        
                        {/* Corner indicators */}
                        <div className={`absolute -top-2 -left-2 w-4 h-4 rounded-full transition-all ${breathPhase?.phase === 'inhale' ? 'bg-pink-500 scale-125 shadow-lg shadow-pink-500/50' : 'bg-indigo-300/50 scale-100'}`} />
                        <div className={`absolute -top-2 -right-2 w-4 h-4 rounded-full transition-all ${breathPhase?.phase === 'hold1' ? 'bg-indigo-500 scale-125 shadow-lg shadow-indigo-500/50' : 'bg-indigo-300/50 scale-100'}`} />
                        <div className={`absolute -bottom-2 -right-2 w-4 h-4 rounded-full transition-all ${breathPhase?.phase === 'exhale' ? 'bg-cyan-500 scale-125 shadow-lg shadow-cyan-500/50' : 'bg-indigo-300/50 scale-100'}`} />
                        <div className={`absolute -bottom-2 -left-2 w-4 h-4 rounded-full transition-all ${breathPhase?.phase === 'hold2' ? 'bg-emerald-500 scale-125 shadow-lg shadow-emerald-500/50' : 'bg-indigo-300/50 scale-100'}`} />
                        
                        {/* Center text */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-2xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                              {breathPhase?.text || "Ready"}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {breathPhase?.phase === 'inhale' && '→ Top'}
                              {breathPhase?.phase === 'hold1' && '↓ Right'}
                              {breathPhase?.phase === 'exhale' && '← Bottom'}
                              {breathPhase?.phase === 'hold2' && '↑ Left'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4-7-8 RELAXING - Moon & Stars */}
                  {selectedPattern.id === '478' && (
                    <div className="relative w-64 h-64 mx-auto mb-4 flex items-center justify-center overflow-hidden">
                      {/* Starry background */}
                      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 rounded-full">
                        {/* Stars */}
                        {[...Array(20)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                            style={{
                              top: `${10 + Math.random() * 80}%`,
                              left: `${10 + Math.random() * 80}%`,
                              animationDelay: `${Math.random() * 2}s`,
                              opacity: 0.3 + Math.random() * 0.7
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Moon */}
                      <div 
                        className="relative transition-all ease-in-out"
                        style={{
                          transform: breathPhase?.phase === 'inhale' 
                            ? 'scale(1.2) translateY(-10px)' 
                            : breathPhase?.phase === 'exhale'
                              ? 'scale(0.8) translateY(10px)'
                              : 'scale(1)',
                          transitionDuration: `${breathPhase?.duration || 1000}ms`
                        }}
                      >
                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-100 via-amber-200 to-amber-300 shadow-2xl shadow-amber-300/50 flex items-center justify-center">
                          {/* Moon craters */}
                          <div className="absolute top-4 left-6 w-4 h-4 rounded-full bg-amber-300/50" />
                          <div className="absolute top-10 right-8 w-3 h-3 rounded-full bg-amber-300/50" />
                          <div className="absolute bottom-6 left-10 w-5 h-5 rounded-full bg-amber-300/50" />
                          
                          <div className="text-center z-10">
                            <p className="text-xl font-bold text-amber-900">{breathPhase?.text || "Ready"}</p>
                            <p className="text-xs text-amber-800 mt-1">
                              {breathPhase?.phase === 'inhale' && '4 seconds'}
                              {breathPhase?.phase === 'hold1' && '7 seconds'}
                              {breathPhase?.phase === 'exhale' && '8 seconds'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Glow around moon */}
                      <div 
                        className="absolute w-40 h-40 rounded-full transition-all ease-in-out"
                        style={{
                          background: 'radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 70%)',
                          transform: breathPhase?.scale === 1 ? 'scale(1.5)' : 'scale(1)',
                          transitionDuration: `${breathPhase?.duration || 1000}ms`
                        }}
                      />
                    </div>
                  )}

                  {/* ENERGIZING - Sun with rays */}
                  {selectedPattern.id === 'energize' && (
                    <div className="relative w-64 h-64 mx-auto mb-4 flex items-center justify-center">
                      {/* Background glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-amber-500/20 to-yellow-500/20 rounded-full blur-xl" />
                      
                      {/* Sun rays */}
                      {[...Array(12)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-2 h-16 origin-bottom transition-all ease-in-out"
                          style={{
                            background: 'linear-gradient(to top, #F59E0B, #FCD34D, transparent)',
                            transform: `rotate(${i * 30}deg) translateY(-90px)`,
                            opacity: breathPhase?.phase === 'inhale' ? 1 : 0.4,
                            height: breathPhase?.phase === 'inhale' ? '80px' : '40px',
                            transitionDuration: `${breathPhase?.duration || 500}ms`
                          }}
                        />
                      ))}
                      
                      {/* Sun core */}
                      <div 
                        className="relative rounded-full bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500 shadow-2xl flex items-center justify-center transition-all ease-in-out"
                        style={{
                          width: breathPhase?.phase === 'inhale' ? '140px' : '100px',
                          height: breathPhase?.phase === 'inhale' ? '140px' : '100px',
                          boxShadow: breathPhase?.phase === 'inhale' 
                            ? '0 0 60px rgba(251,191,36,0.8), 0 0 100px rgba(245,158,11,0.5)' 
                            : '0 0 30px rgba(251,191,36,0.5)',
                          transitionDuration: `${breathPhase?.duration || 500}ms`
                        }}
                      >
                        <div className="text-center">
                          <p className="text-xl font-bold text-orange-900">{breathPhase?.text || "Ready"}</p>
                          <p className="text-2xl">⚡</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CALMING WAVE - Ocean waves */}
                  {selectedPattern.id === 'calm' && (
                    <div className="relative w-64 h-64 mx-auto mb-4 flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-sky-400 via-cyan-500 to-blue-600">
                      {/* Wave layers */}
                      <div 
                        className="absolute w-[200%] h-32 left-[-50%] transition-all ease-in-out"
                        style={{
                          bottom: breathPhase?.phase === 'inhale' ? '60%' : breathPhase?.phase === 'exhale' ? '20%' : '40%',
                          transitionDuration: `${breathPhase?.duration || 1000}ms`
                        }}
                      >
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
                          <path 
                            d="M0,60 C150,120 350,0 500,60 C650,120 850,0 1000,60 C1150,120 1200,60 1200,60 L1200,120 L0,120 Z" 
                            fill="rgba(255,255,255,0.3)"
                          />
                        </svg>
                      </div>
                      <div 
                        className="absolute w-[200%] h-24 left-[-50%] transition-all ease-in-out"
                        style={{
                          bottom: breathPhase?.phase === 'inhale' ? '50%' : breathPhase?.phase === 'exhale' ? '10%' : '30%',
                          transitionDuration: `${(breathPhase?.duration || 1000) * 1.1}ms`
                        }}
                      >
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
                          <path 
                            d="M0,40 C200,100 400,0 600,50 C800,100 1000,20 1200,60 L1200,120 L0,120 Z" 
                            fill="rgba(255,255,255,0.5)"
                          />
                        </svg>
                      </div>
                      <div 
                        className="absolute w-[200%] h-20 left-[-50%] transition-all ease-in-out"
                        style={{
                          bottom: breathPhase?.phase === 'inhale' ? '40%' : breathPhase?.phase === 'exhale' ? '0%' : '20%',
                          transitionDuration: `${(breathPhase?.duration || 1000) * 1.2}ms`
                        }}
                      >
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
                          <path 
                            d="M0,80 C300,20 500,100 700,40 C900,0 1100,80 1200,40 L1200,120 L0,120 Z" 
                            fill="rgba(255,255,255,0.7)"
                          />
                        </svg>
                      </div>
                      
                      {/* Center text */}
                      <div className="relative z-10 text-center">
                        <p className="text-2xl font-bold text-white drop-shadow-lg">{breathPhase?.text || "Ready"}</p>
                        <p className="text-white/80 mt-1">🌊</p>
                      </div>
                    </div>
                  )}

                  {/* Default circle for any other pattern */}
                  {!['box', '478', 'energize', 'calm'].includes(selectedPattern.id) && (
                    <div className="relative w-64 h-64 mx-auto mb-4 flex items-center justify-center">
                      <div 
                        className="rounded-full bg-gradient-to-br from-violet-500 to-pink-500 shadow-2xl flex items-center justify-center transition-all ease-in-out"
                        style={{
                          width: breathPhase?.scale === 1 ? '80%' : '40%',
                          height: breathPhase?.scale === 1 ? '80%' : '40%',
                          transitionDuration: `${breathPhase?.duration || 1000}ms`
                        }}
                      >
                        <p className="text-xl font-bold text-white">{breathPhase?.text || "Ready"}</p>
                      </div>
                    </div>
                  )}

                  {/* Progress indicator */}
                  <div className="flex justify-center gap-2 mb-4">
                    {Array.from({ length: selectedPattern.cycles }).map((_, i) => (
                      <div 
                        key={i}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          i < breathCount 
                            ? 'bg-gradient-to-r from-violet-500 to-pink-500 shadow-lg shadow-purple-500/30' 
                            : i === breathCount 
                              ? 'bg-gradient-to-r from-violet-400 to-pink-400 animate-pulse shadow-md shadow-purple-400/20' 
                              : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Control buttons */}
                  <div className="flex items-center justify-center gap-3">
                    {/* Sound toggle */}
                    <button 
                      className={`px-4 py-2.5 rounded-full border-2 flex items-center gap-2 touch-manipulation transition-all ${
                        breathSoundEnabled 
                          ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                          : 'border-gray-400/50 bg-gray-400/10 text-gray-500'
                      }`}
                      onClick={(e) => { 
                        e.preventDefault(); 
                        setBreathSoundEnabled(!breathSoundEnabled);
                        if (!breathSoundEnabled) {
                          toast.success("Sound enabled 🔔");
                        } else {
                          stopBreathSound();
                          toast.success("Sound muted 🔇");
                        }
                      }}
                      onTouchEnd={(e) => { 
                        e.preventDefault(); 
                        setBreathSoundEnabled(!breathSoundEnabled);
                      }}
                    >
                      {breathSoundEnabled ? (
                        <Volume2 className="w-4 h-4" />
                      ) : (
                        <VolumeX className="w-4 h-4" />
                      )}
                      {breathSoundEnabled ? 'Sound On' : 'Sound Off'}
                    </button>
                    
                    {/* Stop button */}
                    <button 
                      className="px-6 py-2.5 rounded-full border-2 border-violet-500/50 bg-violet-500/10 hover:bg-violet-500/20 text-violet-600 dark:text-violet-400 flex items-center gap-2 touch-manipulation transition-all"
                      onClick={(e) => { e.preventDefault(); stopBreathing(); }}
                      onTouchEnd={(e) => { e.preventDefault(); stopBreathing(); }}
                    >
                      <X className="w-4 h-4" />
                      Stop
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VISUALS MODE */}
          {activeMode === "visuals" && (
            <div className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                Tap anywhere to create calming ripples ✨
              </p>
              <canvas
                ref={canvasRef}
                width={400}
                height={300}
                onClick={handleCanvasClick}
                onTouchStart={handleCanvasTouch}
                onTouchMove={handleCanvasTouch}
                className="w-full rounded-xl cursor-pointer bg-gradient-to-br from-violet-900/30 to-pink-900/30 touch-none"
                data-testid="vibe-canvas"
                style={{ touchAction: 'none' }}
              />
              <p className="text-center text-xs text-muted-foreground mt-2">
                Tap anywhere to create ripples 🌊 Each one carries away a worry
              </p>
            </div>
          )}

          {/* SOUNDS MODE */}
          {activeMode === "sounds" && (
            <div className="space-y-4">
              {/* Streak Display */}
              {streakData && (
                <button
                  onClick={() => setShowStreakModal(true)}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10 border border-amber-500/20 hover:border-amber-500/40 transition-all touch-manipulation"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <Flame className="w-5 h-5 text-orange-500" />
                        <span className="text-xl font-bold text-orange-600">{streakData.streak?.current_streak || 0}</span>
                      </div>
                      <div className="text-left">
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">Day Streak</p>
                        <p className="text-[10px] text-muted-foreground">
                          {streakData.meditated_today ? "✓ Meditated today" : "Meditate to keep streak"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {streakData.milestones?.earned?.slice(-3).map((m, i) => (
                        <span key={i} className="text-lg" title={m.title}>{m.badge}</span>
                      ))}
                      {streakData.milestones?.next && (
                        <span className="text-[10px] text-muted-foreground">
                          {streakData.milestones.next.days_remaining}d to {streakData.milestones.next.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )}
              
              <h3 className="font-semibold text-center">Sound Bath</h3>
              <p className="text-xs text-muted-foreground text-center">Set a timer or meditate freely</p>
              
              {/* Timer Presets */}
              <div className="flex justify-center gap-2 flex-wrap">
                {timerPresets.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => setTimerGoal(preset.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all touch-manipulation ${
                      timerGoal === preset.value
                        ? "bg-amber-500 text-white shadow-lg shadow-amber-500/30"
                        : "bg-muted hover:bg-amber-500/20 text-muted-foreground hover:text-amber-600"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                {timerGoal ? `⏱️ ${timerGoal / 60} min session` : "∞ Unlimited - stop when ready"}
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                {soundscapes.map(sound => (
                  <button 
                    key={sound.id}
                    className={`p-3 rounded-xl text-center transition-all cursor-pointer touch-manipulation select-none ${
                      selectedSound?.id === sound.id 
                        ? "border-2 border-amber-500 bg-amber-500/20 shadow-lg" 
                        : "border-2 border-transparent bg-card hover:border-amber-500/50 hover:bg-amber-500/5"
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isSoundPlaying && selectedSound?.id === sound.id) {
                        stopSound();
                      } else {
                        playSound(sound);
                      }
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      if (isSoundPlaying && selectedSound?.id === sound.id) {
                        stopSound();
                      } else {
                        playSound(sound);
                      }
                    }}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span className="text-2xl block mb-1 pointer-events-none">{sound.emoji}</span>
                    <p className="text-xs font-medium pointer-events-none">{sound.name}</p>
                    {sound.frequency && (
                      <span className="text-[10px] text-amber-600 pointer-events-none">{sound.frequency}</span>
                    )}
                    {isSoundPlaying && selectedSound?.id === sound.id && (
                      <div className="mt-2 pointer-events-none">
                        <div className="flex justify-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <div 
                              key={i} 
                              className="w-1 bg-amber-500 rounded-full animate-pulse"
                              style={{ 
                                height: `${10 + Math.random() * 15}px`,
                                animationDelay: `${i * 0.1}s`
                              }}
                            />
                          ))}
                        </div>
                        <p className="text-[9px] text-amber-600 mt-1">Playing...</p>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {isSoundPlaying && (
                <div className="space-y-3">
                  <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-amber-700 dark:text-amber-400 font-medium text-sm">
                        {selectedSound?.name}
                      </span>
                    </div>
                    
                    {/* Show countdown if goal is set, otherwise show elapsed time */}
                    {timerGoal && timerRemaining !== null ? (
                      <div className="text-center">
                        <span className="text-3xl font-mono text-amber-600 dark:text-amber-300">
                          {formatDuration(timerRemaining)}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">remaining</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <span className="text-3xl font-mono text-amber-600 dark:text-amber-300">
                          {formatDuration(soundDuration)}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">elapsed</p>
                      </div>
                    )}
                    
                    {/* Progress bar for timed sessions */}
                    {timerGoal && timerRemaining !== null && (
                      <div className="w-full h-2 bg-amber-200/30 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-1000"
                          style={{ width: `${((timerGoal - timerRemaining) / timerGoal) * 100}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <Button variant="outline" className="w-full border-amber-500/50 hover:bg-amber-500/10" onClick={stopSound}>
                    <VolumeX className="w-4 h-4 mr-2" />
                    Stop Sound Bath
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* AFFIRMATIONS MODE */}
          {activeMode === "affirmations" && (
            <div className="space-y-4">
              {affirmation ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-4xl animate-pulse">
                    {affirmation.emoji}
                  </div>
                  <p className="text-xl font-serif italic text-primary mb-2">
                    "{affirmation.text}"
                  </p>
                  <span className="text-xs bg-purple-500/10 text-purple-600 px-3 py-1 rounded-full">
                    {affirmation.category}
                  </span>
                  
                  <div className="flex justify-center gap-3 mt-6">
                    <button 
                      className="px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent flex items-center gap-1 text-sm touch-manipulation active:scale-95 transition-transform"
                      onClick={(e) => { e.preventDefault(); fetchAffirmation(); }}
                      onTouchEnd={(e) => { e.preventDefault(); fetchAffirmation(); }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      New
                    </button>
                    <button 
                      className="px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent flex items-center gap-1 text-sm touch-manipulation active:scale-95 transition-transform"
                      onClick={(e) => { e.preventDefault(); saveAffirmation(); }}
                      onTouchEnd={(e) => { e.preventDefault(); saveAffirmation(); }}
                    >
                      <Heart className="w-4 h-4" />
                      Save
                    </button>
                    <button 
                      className="px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent flex items-center gap-1 text-sm touch-manipulation active:scale-95 transition-transform"
                      onClick={(e) => { e.preventDefault(); shareAffirmation(); }}
                      onTouchEnd={(e) => { e.preventDefault(); shareAffirmation(); }}
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Loading your affirmation...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      
      {/* Streak Modal */}
      <Dialog open={showStreakModal} onOpenChange={setShowStreakModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Meditation Journey
            </DialogTitle>
          </DialogHeader>
          
          {streakData && (
            <div className="space-y-4">
              {/* Current Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className="text-2xl font-bold text-orange-600">{streakData.streak?.current_streak || 0}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Current Streak</p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Trophy className="w-4 h-4 text-purple-500" />
                    <span className="text-2xl font-bold text-purple-600">{streakData.streak?.longest_streak || 0}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Best Streak</p>
                </div>
              </div>
              
              {/* Total Stats */}
              <div className="flex justify-around p-3 rounded-xl bg-muted/50">
                <div className="text-center">
                  <p className="text-lg font-semibold">{streakData.streak?.total_sessions || 0}</p>
                  <p className="text-xs text-muted-foreground">Sessions</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold">{streakData.streak?.total_minutes || 0}</p>
                  <p className="text-xs text-muted-foreground">Minutes</p>
                </div>
              </div>
              
              {/* Next Milestone */}
              {streakData.milestones?.next && (
                <div className="p-3 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl opacity-50">{streakData.milestones.next.badge}</span>
                      <div>
                        <p className="text-sm font-medium">{streakData.milestones.next.title}</p>
                        <p className="text-xs text-muted-foreground">{streakData.milestones.next.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-600">{streakData.milestones.next.days_remaining}</p>
                      <p className="text-[10px] text-muted-foreground">days to go</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Earned Milestones */}
              {streakData.milestones?.earned?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Earned Badges</p>
                  <div className="flex flex-wrap gap-2">
                    {streakData.milestones.earned.map((m, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30"
                        title={m.description}
                      >
                        <span className="text-lg">{m.badge}</span>
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400">{m.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* All Milestones Preview */}
              <div>
                <p className="text-sm font-medium mb-2">All Milestones</p>
                <div className="grid grid-cols-3 gap-2">
                  {streakData.milestones?.all?.map((m, i) => {
                    const isEarned = streakData.milestones.earned?.some(e => e.days === m.days);
                    return (
                      <div 
                        key={i}
                        className={`p-2 rounded-lg text-center transition-all ${
                          isEarned 
                            ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30" 
                            : "bg-muted/30 opacity-50"
                        }`}
                      >
                        <span className="text-xl block">{m.badge}</span>
                        <p className="text-[10px] font-medium">{m.days}d</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default VibeOutHub;
